import { ChangeDetectionStrategy, Component, output, inject, ViewEncapsulation, computed, input } from '@angular/core';
import { IOnboardingStepNotification } from '../../../fi-onboarding';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import {
    FiTooltipButtonIconColors,
    FiTooltipIconClassType,
} from '@fi-shared/fi-icon-tooltip-button/fi-icon-tooltip-button.component';
import { CheckpointActionRecipientHighlightedTypes } from '@fi-shared/checklist/checklist-consts';
import { AppTextService } from '@fi-shared/app-text/app-text.service';
import { FiOnboardingStepNotificationRecipientType } from '@fi-app/fi-onboarding/fi-onboarding-enums';
import { ObjectTypes } from '@fi-shared/types';
import { FiOnboardingBuilderStoreService } from '../../../fi-onboarding-builder-store.service';
import { IAccessRightFilterCollection } from '@fi-shared/models';
import { FiOnboardingAutomatedNotificationHelperService } from '../fi-onboarding-automated-notification-helper.service';

@Component({
    selector: 'fi-onboarding-notification-tab-content',
    templateUrl: './notification-tab-content.component.html',
    styleUrls: ['./notification-tab-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-onboarding-notification-tab-content' },
    standalone: false,
})
export class FiOnboardingNotificationTabContentComponent {
    public notifications = input.required<IOnboardingStepNotification[]>();
    public isParticipantsNotification = input<boolean>(false);

    public showAddNotificationDialog = output<void>();
    public editNotification = output<IOnboardingStepNotification>();
    public deleteNotification = output<IOnboardingStepNotification>();

    private appTextService = inject(AppTextService);
    private fiOnboardingBuilderStoreService = inject(FiOnboardingBuilderStoreService);
    private notifHelperService = inject(FiOnboardingAutomatedNotificationHelperService);

    protected readonly FiTooltipIconClassType = FiTooltipIconClassType;
    protected readonly FiInfoIconMode = FiInfoIconMode;
    protected readonly FiTooltipButtonIconColors = FiTooltipButtonIconColors;
    protected readonly ObjectTypes = ObjectTypes;
    protected readonly accessRightFilterCollection = computed<IAccessRightFilterCollection>(() => {
        return this.fiOnboardingBuilderStoreService.onboardingAccessRightFilterCollection();
    });

    protected readonly CheckpointActionRecipientHighlightedTypes = CheckpointActionRecipientHighlightedTypes;
    protected readonly recipientLabels = this.notifHelperService.RecipientTypeHighlightedOptions;

    protected getSelectedItemIds(notification: IOnboardingStepNotification): number[] {
        if (
            notification.RecipientType === FiOnboardingStepNotificationRecipientType.Roles ||
            notification.RecipientType === FiOnboardingStepNotificationRecipientType.RolesLocal
        ) {
            return notification.RoleIds;
        }

        if (notification.RecipientType === FiOnboardingStepNotificationRecipientType.Users) {
            return notification.UserIds;
        }
    }

    protected getActionTargetLabel(notification: IOnboardingStepNotification): string {
        const selectedReceiversLabel = CheckpointActionRecipientHighlightedTypes.find((option) => {
            return option.Id === notification.RecipientType;
        });
        return (
            this.appTextService.get(selectedReceiversLabel.Name) +
            (this.isLocalRolesRecipientType(notification) || this.isRoleRecipientType(notification) ? '' : '.')
        );
    }

    protected isLocalRolesRecipientType(notification: IOnboardingStepNotification): boolean {
        return notification.RecipientType === FiOnboardingStepNotificationRecipientType.RolesLocal;
    }

    protected isRoleRecipientType(notification: IOnboardingStepNotification): boolean {
        return notification.RecipientType === FiOnboardingStepNotificationRecipientType.Roles;
    }
}
